/* UI only: native number inputs own ArrowUp/ArrowDown; context adapters own I/O. */
(() => {
  if (customElements.get('inventa-quantity')) return;

  class InventaQuantity extends HTMLElement {
    static observedAttributes = ['busy', 'disabled'];

    connectedCallback() {
      if (this.controller) return;
      this.input = this.querySelector('[data-inventa-quantity-input]');
      if (!this.input) return;
      this.message = this.querySelector('.inventa-quantity__error');
      this.committed = this.input.defaultValue;
      this.controller = new AbortController();
      const options = {signal: this.controller.signal};
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-inventa-quantity-step]');
        if (!button || !this.contains(button) || button.disabled || this.hasAttribute('busy') || this.hasAttribute('disabled')) return;
        if (!this.input.validity.valid) this.reset(this.committed);
        this.input.stepUp(Number(button.dataset.inventaQuantityStep));
        this.input.dispatchEvent(new Event('input', {bubbles: true}));
        this.input.dispatchEvent(new Event('change', {bubbles: true}));
      }, options);
      this.input.addEventListener('input', () => {
        this.clearError();
        this.sync();
      }, options);
      this.input.addEventListener('change', () => this.commit(), options);
      this.input.addEventListener('blur', () => {
        // An empty editing state never represents a deletion or backend update.
        if (this.input.value === '' && !this.input.validity.badInput) this.reset(this.committed);
      }, options);
      this.input.addEventListener('invalid', () => this.setError(this.input.validationMessage), options);
      this.input.addEventListener('keydown', event => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        this.commit();
      }, options);
      this.sync();
    }

    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    attributeChangedCallback() { this.sync(); }

    clearError() {
      this.removeAttribute('invalid');
      this.input?.removeAttribute('aria-invalid');
      if (this.message) { this.message.hidden = true; this.message.textContent = ''; }
    }

    setError(message) {
      this.setAttribute('invalid', '');
      this.input?.setAttribute('aria-invalid', 'true');
      if (this.message) { this.message.textContent = message; this.message.hidden = false; }
    }

    reset(value) {
      this.committed = String(value);
      this.input.value = this.committed;
      this.input.defaultValue = this.committed;
      this.clearError();
      this.sync();
    }

    commit() {
      if (this.hasAttribute('busy') || this.hasAttribute('disabled')) return;
      if (this.input.value === '' && !this.input.validity.badInput) { this.reset(this.committed); return; }
      if (!this.input.validity.valid || !Number.isInteger(this.input.valueAsNumber)) {
        this.setError(this.input.validationMessage);
        return;
      }
      const value = this.input.valueAsNumber;
      const previous = Number(this.committed);
      this.committed = this.input.value;
      this.clearError();
      this.sync();
      if (value !== previous) this.dispatchEvent(new CustomEvent('inventa:quantity-change', {bubbles: true, detail: {value, previous, input: this.input}}));
    }

    sync() {
      if (!this.input) return;
      const disabled = this.hasAttribute('disabled');
      const busy = this.hasAttribute('busy');
      const blocked = disabled || busy;
      this.toggleAttribute('aria-busy', false);
      if (this.hasAttribute('busy')) this.setAttribute('aria-busy', 'true');
      // Busy must preserve the browser's in-progress Tab destination. A native
      // disabled input/step button interrupts blur/change keyboard navigation.
      this.input.disabled = disabled;
      this.input.readOnly = busy;
      const value = this.input.valueAsNumber;
      const active = this.ownerDocument.activeElement;
      let focusInput = false;
      this.querySelectorAll('[data-inventa-quantity-step]').forEach(button => {
        const decreases = Number(button.dataset.inventaQuantityStep) < 0;
        const atLimit = decreases ? value <= Number(this.input.min) : this.input.max !== '' && value >= Number(this.input.max);
        if (!blocked && atLimit && button === active) focusInput = true;
        button.disabled = disabled || atLimit;
        if (busy) button.setAttribute('aria-disabled', 'true');
        else button.removeAttribute('aria-disabled');
      });
      // Disabling the focused limit button would otherwise send focus to the body.
      if (focusInput) this.input.focus({preventScroll: true});
    }
  }
  customElements.define('inventa-quantity', InventaQuantity);
})();
